/**
 * The run's copies of the pet rules (Pets v2, docs/PETS.md): `LevelModifiers.pet` (the pet taken into the battle, at its
 * level, with each unlocked ability's numbers resolved there: meta/petAbilities.ts petRule) and `LevelModifiers.wildPet`
 * (the wild encounter's inputs from the app: which pet, the skin chance after a treat, the skins it may wear). Both are
 * inputs of the run like the seed: the replay record carries them and the state hash takes each only when present, so
 * a run without them hashes exactly as before. A v1 pet block (`bond`, Muster only) still reads, as a level-`bond` pet
 * with Muster alone, and hashes as it did, so a battle suspended by a v1 build still proves itself on resume.
 */
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { PetKit, PetKitJson, hashPetKit, petKitFromJson, petKitToJson } from "./petKit";

/** Bite at the run's level: `damage` every `intervalTicks` to the ground raider nearest the flag within `reach` cells. */
export interface BiteRule { readonly damage: Fix64; readonly intervalTicks: number; readonly reach: Fix64 }
/** Rally Bark: every `everyTicks`, `durationTicks` of +`damagePercent` dealt and −`guardPercent` taken for troops within `reach`. */
export interface BarkRule { readonly everyTicks: number; readonly durationTicks: number; readonly reach: Fix64; readonly damagePercent: number; readonly guardPercent: number }
/** Howl: once a wave, ground raiders within `reach` of the flag stand stunned `stunTicks` (bosses `bossStunTicks`). */
export interface HowlRule { readonly reach: Fix64; readonly stunTicks: number; readonly bossStunTicks: number }

/**
 * A pet on the rally flag: which pet, the level it plays at, and each unlocked ability's numbers there (an ability
 * still locked at that level is absent). Muster: the fortress's troop spawn interval is cut by `spawnCutPercent`. The
 * pup's abilities are `bite` / `bark` / `howl`; the Pets v3 pets' the general kinds (`strike`, `aura`, `pulse`,
 * `signature`: sim/petKit.ts).
 */
export interface PetRule extends PetKit {
  readonly id: string;
  readonly level: number;
  readonly spawnCutPercent: number;
  readonly bite?: BiteRule;
  readonly bark?: BarkRule;
  readonly howl?: HowlRule;
}

/**
 * The wild encounter's run inputs: the pet, the skin chance (basis points, the armed treat's bonus included), the skins
 * it may wear (catalogue ids, in order) and the Pet Treat armed for it ("" = none: the app uses it up on WildPetAppeared).
 */
export interface WildPetRule {
  readonly id: string; readonly skinChanceBp: number; readonly skins: readonly string[]; readonly treat: string;
  /** The player has never caught this pet: it comes with the data's firstCatchHpMult (absent: false, hashed only when true). */
  readonly firstCatch?: boolean;
}

const FIRST_CATCH_HASH_TAG = 0xf1c7, PET_HASH_TAG = 0x9e75, BITE_HASH_TAG = 0xb17e, BARK_HASH_TAG = 0xba4c, HOWL_HASH_TAG = 0x4041, WILD_HASH_TAG = 0x3d1d;

export function hashPetRule(h: Fnv1a64, p: PetRule): void {
  // the v1 layout first (its `bond` is the level), so a v1 pet block hashes as it always did
  h.writeInt(PET_HASH_TAG); h.writeString(p.id); h.writeInt(p.level); h.writeInt(p.spawnCutPercent);
  if (p.bite) { h.writeInt(BITE_HASH_TAG); h.writeRaw(p.bite.damage.raw); h.writeInt(p.bite.intervalTicks); h.writeRaw(p.bite.reach.raw); }
  if (p.bark) { const b = p.bark; h.writeInt(BARK_HASH_TAG); h.writeInt(b.everyTicks); h.writeInt(b.durationTicks); h.writeRaw(b.reach.raw); h.writeInt(b.damagePercent); h.writeInt(b.guardPercent); }
  if (p.howl) { const w = p.howl; h.writeInt(HOWL_HASH_TAG); h.writeRaw(w.reach.raw); h.writeInt(w.stunTicks); h.writeInt(w.bossStunTicks); }
  hashPetKit(h, p);                                       // Pets v3 kinds: nothing for the pup
}

export function hashWildPetRule(h: Fnv1a64, w: WildPetRule): void {
  h.writeInt(WILD_HASH_TAG); h.writeString(w.id); h.writeInt(w.skinChanceBp); h.writeInt(w.skins.length);
  for (const s of w.skins) h.writeString(s);
  h.writeString(w.treat);
  if (w.firstCatch === true) h.writeInt(FIRST_CATCH_HASH_TAG);
}

/** Stored forms: Fix64 numbers as raw decimal strings (like `enemyHpMult`). */
export interface PetRuleJson extends PetKitJson {
  id: string;
  level?: number;
  /** v1 records only (read, never written): the Bond the pup played at, read as its level. */
  bond?: number;
  spawnCutPercent: number;
  bite?: { damage: string; intervalTicks: number; reach: string };
  bark?: { everyTicks: number; durationTicks: number; reach: string; damagePercent: number; guardPercent: number };
  howl?: { reach: string; stunTicks: number; bossStunTicks: number };
}
export interface WildPetRuleJson { id: string; skinChanceBp: number; skins: string[]; treat?: string; firstCatch?: boolean }

export function petRuleToJson(p: PetRule): PetRuleJson {
  const out: PetRuleJson = { id: p.id, level: p.level, spawnCutPercent: p.spawnCutPercent };
  if (p.bite) out.bite = { damage: p.bite.damage.raw.toString(), intervalTicks: p.bite.intervalTicks, reach: p.bite.reach.raw.toString() };
  if (p.bark) out.bark = { ...p.bark, reach: p.bark.reach.raw.toString() };
  if (p.howl) out.howl = { ...p.howl, reach: p.howl.reach.raw.toString() };
  return { ...out, ...petKitToJson(p) };
}

export function wildPetRuleToJson(w: WildPetRule): WildPetRuleJson { return { id: w.id, skinChanceBp: w.skinChanceBp, skins: [...w.skins], ...(w.treat !== "" ? { treat: w.treat } : {}), ...(w.firstCatch === true ? { firstCatch: true } : {}) }; }

/** Highest stored percent a pet's cut may carry (data/pets.ts allows at most 90); a bark's damage bonus up to 500. */
const MAX_PET_PERCENT = 90, MAX_BONUS_PERCENT = 500, BASIS_POINTS = 10_000;
const ID = /^[A-Za-z0-9_.]{1,60}$/;
const whole = (v: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): v is number => Number.isInteger(v) && (v as number) >= min && (v as number) <= max;
const raw = (v: unknown): v is string => typeof v === "string" && /^\d{1,30}$/.test(v) && BigInt(v) > 0n;
const obj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/** A stored pet block (v2, or a v1 one: `bond` for `level`, Muster only); null when malformed. */
export function petRuleFromJson(v: unknown): PetRule | null {
  if (!obj(v) || typeof v.id !== "string" || !ID.test(v.id)) return null;
  const level = v.level ?? v.bond;
  if (!whole(level, 1) || !whole(v.spawnCutPercent, 0, MAX_PET_PERCENT)) return null;
  let rule: PetRule = { id: v.id, level, spawnCutPercent: v.spawnCutPercent };
  if (v.bite !== undefined) {
    const b = v.bite;
    if (!obj(b) || !raw(b.damage) || !whole(b.intervalTicks, 1) || !raw(b.reach)) return null;
    rule = { ...rule, bite: { damage: Fix64.fromRaw(BigInt(b.damage)), intervalTicks: b.intervalTicks, reach: Fix64.fromRaw(BigInt(b.reach)) } };
  }
  if (v.bark !== undefined) {
    const b = v.bark;
    if (!obj(b) || !whole(b.everyTicks, 1) || !whole(b.durationTicks, 1) || !raw(b.reach) || !whole(b.damagePercent, 0, MAX_BONUS_PERCENT) || !whole(b.guardPercent, 0, MAX_PET_PERCENT)) return null;
    rule = { ...rule, bark: { everyTicks: b.everyTicks, durationTicks: b.durationTicks, reach: Fix64.fromRaw(BigInt(b.reach)), damagePercent: b.damagePercent, guardPercent: b.guardPercent } };
  }
  if (v.howl !== undefined) {
    const w = v.howl;
    if (!obj(w) || !raw(w.reach) || !whole(w.stunTicks, 1) || !whole(w.bossStunTicks, 0)) return null;
    rule = { ...rule, howl: { reach: Fix64.fromRaw(BigInt(w.reach)), stunTicks: w.stunTicks, bossStunTicks: w.bossStunTicks } };
  }
  const kit = petKitFromJson(v);
  return kit === null ? null : { ...rule, ...kit };
}

/** A stored wild-pet block; null when malformed. */
export function wildPetRuleFromJson(v: unknown): WildPetRule | null {
  if (!obj(v) || typeof v.id !== "string" || !ID.test(v.id) || !whole(v.skinChanceBp, 0, BASIS_POINTS)) return null;
  if (!Array.isArray(v.skins) || !v.skins.every((s) => typeof s === "string" && ID.test(s))) return null;
  if (v.treat !== undefined && (typeof v.treat !== "string" || !ID.test(v.treat))) return null;
  if (v.firstCatch !== undefined && typeof v.firstCatch !== "boolean") return null;
  const rule: WildPetRule = { id: v.id, skinChanceBp: v.skinChanceBp, skins: [...(v.skins as string[])], treat: typeof v.treat === "string" ? v.treat : "" };
  return v.firstCatch === true ? { ...rule, firstCatch: true } : rule;
}
